import React from 'react';
import moment from 'moment';
import { __ } from 'i18n/index';
import { formatDate, formatNumber } from 'i18n/locale';
import { DateFormat } from 'components/DateFormat';
import useMounted from 'react/hooks/useMounted';

const SAME_YEAR = { month: 'short', day: 'numeric' };
const OTHER_YEAR = { month: 'short', day: 'numeric', year: 'numeric' };
const OVER_A_YEAR = { month: 'short', year: 'numeric' };
const ago = (count, unit) => formatNumber(count, { style: 'unit', unit, unitDisplay: 'narrow' });

// Short label for how long ago a time was, in the site's language: "Just now",
// "55m", "3h", "Sep 3", "Dec 14, 2025", "Dec 2024". A future time shows its full
// date.
export function relativeLadderTime(time, now = moment()) {
	if(time.year() !== now.year()) {
		return formatDate(time, now.diff(time, 'days') < 365 ? OTHER_YEAR : OVER_A_YEAR);
	}
	if(time.isAfter(now)) {
		return formatDate(time, OTHER_YEAR);
	}
	if(time.isSame(now, 'day')) {
		const hours = now.diff(time, 'hours');
		if(hours) {
			return ago(hours, 'hour');
		}
		const minutes = now.diff(time, 'minutes');
		return minutes ? ago(minutes, 'minute') : __('Just now');
	}
	return formatDate(time, SAME_YEAR);
}

const FORMATS = {
	relative: (time) => relativeLadderTime(time),
	clock: (time) => formatDate(time, { hour: 'numeric', minute: '2-digit' }),
	date: (time) => formatDate(time, { dateStyle: 'long' }),
	shortDate: (time) => formatDate(time, OTHER_YEAR),
	monthYear: (time) => formatDate(time, OVER_A_YEAR),
	clockWithSeconds: (time) => formatDate(time, { timeStyle: 'medium' }),
	full: (time) => formatDate(time, { dateStyle: 'long', timeStyle: 'short' }),
	fullWithWeekday: (time) => formatDate(time, { dateStyle: 'full', timeStyle: 'short' }),
};

// A moment as one of the formats above, in the site's language and the
// viewer's zone. Only call it after mount (or in the browser), since the
// server doesn't know the viewer's zone.
export function formatLadderTime(time, format) {
	return FORMATS[format](time);
}

// A time the server sent as a unix timestamp (or { timestamp }), shown in the
// viewer's zone with the full date as its title. The server render and the
// hydrating render leave it blank, since the server doesn't know the zone.
export default function LadderTime({ time, format = 'relative', className }) {
	const mounted = useMounted();
	const instant = DateFormat.momentFromLadderTime(time);
	if(!instant) {
		return null;
	}
	return (
		<time className={className} dateTime={instant.toISOString()} title={mounted ? FORMATS.full(instant) : undefined}>
			{mounted ? FORMATS[format](instant) : null}
		</time>
	);
}
